﻿import React, { useState, useEffect } from 'react';
import { NoteObject } from '../models/note-object';
import { CrudFunctions } from '../utilities/crud-functions';
import { EditorConstants } from '../store/editor-constants';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { MessageBoxReplyType, ModalMessageType } from '../models/enums';
import { NoteType } from '../models/enums';
import DmaFooter from './DmaFooter';

import HtmlEditor, {
    TableContextMenu,
    TableResizing,
    Toolbar, MediaResizing, Item
} from 'devextreme-react/html-editor';

import { TextBox } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';
import { MessageBoxReply } from '../models/message-box-reply';

const DELETE_NOTE_WARNING = 1;
const LEAVE_WITHOUT_SAVING_WARNING = 2;

export interface Props {
    noteType?: NoteType;
    parentId?: string;
    height?: string;
    onCancel?: () => void;
}

export default function NoteEditor(props: Props) {
    const [note, setNote] =
        useState<NoteObject>();
        
    const [loadPanelVisible, setLoadPanelVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [areChanges, setAreChanges] =
        useState<boolean>(false);

    const [dataLoaded, setDataLoaded] =
        useState<boolean>(false);

    useEffect(() => {
        getNote();
    }, []);

    return (
        <div className="pw-form-container">
            <div className="pw-form-section">
                <div className="pw-comp-row pw-margin-top-05">
                    <div className="pw-form-unit pw-comp-30">
                        <div className="pw-form-unit-label">Title</div>
                        <TextBox id="title" placeholder="na"
                            value={note && note.title ?
                                note.title : undefined}
                            onValueChanged={onValueChanged} />
                    </div>
                    <div className="title-bar-spacer" />
                    <div className="save-toolbar pw-margin-right-05">
                        <Button id="saveButton" text="Save" icon="save" type="success"
                            className="pw-button"
                            hint="save changes"
                            height={'1.8rem'}
                            onClick={onSaveClick} />

                        <Button id="deleteNoteButton" text="Delete" icon="clear" type="normal"
                            className="pw-button" hint="delete note"
                            height={'1.8rem'}
                            onClick={onDeleteNoteClick} />

                        <Button id="cancelButton" text="Close" icon="close" type="normal"
                            className="pw-button"
                            hint="close dialog"
                            height={'1.8rem'}
                            onClick={onCloseClick} />
                    </div>
                </div>

                <div className="pw-form-unit">
                    <div className="pw-form-unit-label">Description</div>
                    <TextBox id="description" placeholder="na" className="textbox"
                        value={note && note.description ?
                            note.description : undefined}
                        onValueChanged={onValueChanged} />
                </div>
            </div>

            <div id="DocumentEditorContainer"
                className="pw-margin-left-05" >
                <HtmlEditor height={props.height ? props.height : "80vh"}
                    value={note && note.text ? note.text : undefined}
                    onValueChanged={onEditorContentChanged} >

                    <MediaResizing enabled={true} />
                    <TableContextMenu enabled={true} />
                    <TableResizing enabled={true} />

                    <Toolbar multiline={true} >
                        <Item name="separator" />
                        <Item name="undo" />
                        <Item name="redo" />
                        <Item name="separator" />
                        <Item name="size" acceptedValues={EditorConstants.FontSizes} />
                        <Item name="font" acceptedValues={EditorConstants.FontFamilies} />
                        <Item name="separator" />
                        <Item name="bold" />
                        <Item name="italic" />
                        <Item name="strike" />
                        <Item name="underline" />
                        <Item name="separator" />
                        <Item name="alignLeft" />
                        <Item name="alignCenter" />
                        <Item name="alignRight" />
                        <Item name="alignJustify" />
                        <Item name="separator" />
                        <Item name="orderedList" />
                        <Item name="bulletList" />
                        <Item name="separator" />
                        <Item name="header" acceptedValues={EditorConstants.HtmlHeaderValues} />
                        <Item name="separator" />
                        <Item name="color" />
                        <Item name="background" />
                        <Item name="separator" />
                        <Item name="link" />
                        <Item name="image" />
                        <Item name="separator" />
                        <Item name="clear" />
                        <Item name="codeBlock" />
                        <Item name="blockquote" />
                        <Item name="separator" />
                        <Item name="insertTable" />
                        <Item name="deleteTable" />
                        <Item name="insertRowAbove" />
                        <Item name="insertRowBelow" />
                        <Item name="deleteRow" />
                        <Item name="insertColumnLeft" />
                        <Item name="insertColumnRight" />
                        <Item name="deleteColumn" />
                    </Toolbar>
                </HtmlEditor>
            </div>

            <DmaFooter />

            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                showPane={true} />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onReply={onGlobalDialogReply} onClose={onGlobalDialogClose} />
            }
        </div>
    );

    function onEditorContentChanged(e: any) {
        if (!dataLoaded)
            return;

        //this is needed because what seems to be a DE bug- if the event is undefined/null then it is not
        // a legit event firing
        if (!e.event)
            return;

        //console.log("onEditorContentChanged");
        //console.log(e);

        setNote({ ...note, text: e.value });
        setAreChanges(true);
    }

    function onValueChanged(e: any) {
        if (!dataLoaded)
            return;

        if (!note)
            return;

        const caller = e.element.id;
        const currentValue = note[caller];

        if (currentValue === e.value)
            return;
        else if (!currentValue && !e.value)
            return;
        else {
            const updatedNote = { ...note, [caller]: e.value };

            setNote(updatedNote);
            setAreChanges(true);
        }
    }

    // get functions

    async function getNote() {
        await CrudFunctions.getData(`notes/notesbyparentidandnotetype/${props.parentId}/${props.noteType}`)
            .then(data => {
                if (data && data.length > 0) {
                    let returnedNote = data[0] as NoteObject;

                    if (returnedNote) {
                        returnedNote.text = atob(returnedNote.base64Blob);

                        setNote(returnedNote);
                    }
                }
                else { //create a new note
                    let newNote: NoteObject = {
                        noteType: props.noteType,
                        parentId: props.parentId,
                    }

                    setNote(newNote);
                }

                setLoadPanelVisible(false);
                setDataLoaded(true);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Note");
            });
    }

    //PUT functions

    async function upsertNote(note: NoteObject) {
        return CrudFunctions.putData("notes/note", note);
    }

    // DELETE functions

    async function deleteNote(noteId: string) {
        return CrudFunctions.deleteData(`notes/note/${noteId}`);
    }

    async function onSaveClick(e: any) {
        await saveChanges();
    }

    function onDeleteNoteClick() {
        if (note && note.noteId) {
            showPromptMessage(ModalMessageType.PromptYesNo,
                "Delete Note Confirmation",
                "Are you sure you want to delete this note? This operation cannot be undone. Do you want to proceed?",
                DELETE_NOTE_WARNING);
        }
    }

    async function onDeleteNote() {
        if (note && note.noteId) {
            await deleteNote(note.noteId)
                .then(() => {
                    if (props.onCancel)
                        props.onCancel();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Note");
                });
        }
    }

    function onCloseClick(e: any) {
        if (areChanges) {
            showPromptMessage(ModalMessageType.PromptYesNo,
                "Pending Changes",
                "There are pending changes. Do you want to exit anyway?",
                LEAVE_WITHOUT_SAVING_WARNING);
        }
        else {
            if (props.onCancel)
                props.onCancel();
        }
    }

    function hideLoadPanel() {
        setLoadPanelVisible(false);
    }

    async function saveChanges() {
        let currentNote = { ...note };
        
        if(currentNote && currentNote.text)
            currentNote.base64Blob = btoa(note.text);
            
        await upsertNote(currentNote)
            .then((data) => {
                setAreChanges(false);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Note");
            });
    }

    //other
    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

    function onGlobalDialogReply(reply: MessageBoxReply) {
        if (reply.code === DELETE_NOTE_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;

            onDeleteNote();
        }
        else if (reply.code === LEAVE_WITHOUT_SAVING_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;

            if (props.onCancel)
                props.onCancel();
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showPromptMessage(mode: ModalMessageType, title: string, message: string, code: number) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message,
            code: code
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

